import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetcher } from "services/api.service";
import useSWR from "swr";

// Ask again until ShipStation has attached the PDF. SWR's refreshInterval callback is
// only read when its effect restarts, and that effect also stops retrying after an error,
// so the first pack attempt could stay on this spinner until a refresh.
const LABEL_RETRY_MS = 1000;

// Printing in Mobile Safari is rough.  Printing via iframe can work, but the scale is not correct.
// Opening a new window and printing directly from there works, but the window will not close automatically.
// This function uses the latter approach and tries to close the window after a delay.
// Taken from:
// https://github.com/crabbly/Print.js/issues/44#issuecomment-1550317552
function printInWindowSafariMobileHack(url: string) {
  const printWindow = window.open(url);
  if (!printWindow) throw new Error("Failed to open print window");
  printWindow.onload = () => {
    // Note using a zero delay setTimeout here to workaround issue that even when putting
    // print() inside of onload iOS Safari will prompt that the page hasn't loaded completely before print was called
    printWindow.setTimeout(() => {
      printWindow.focus();
      printWindow.print();

      // Try to automatically close the window after print
      // This is iOS Safari specific behavior we're relying on here. Essentially, there's not a good callback
      // currently to detect that the print dialog closed because
      // 1) "afterprint" event is only supported if the top level page is HTML where as it's PDF likely in this case
      // 2) there doesn't appear to be any other events (like focus) that we could hook into to detect
      // This works in iOS because if you call window.close() while the print dialog is open nothing happens. This isn't
      // true to other browsers though. Because of this though we can just periodically try to close the window until it works.
      printWindow.setInterval(() => printWindow.close(), 300);
    }, 500);
  };
}

interface ShippingLabelData {
  url: string | null;
  status: string;
  error?: string | null;
}

function requestStatus(error: unknown) {
  if (typeof error !== "object" || error === null || !("response" in error)) return undefined;
  const response = error.response;
  if (typeof response !== "object" || response === null || !("status" in response)) return undefined;
  return typeof response.status === "number" ? response.status : undefined;
}

// A 404 is a file ShipStation has not attached yet. Any other failure is the same file.
function missingLabelFile(error: unknown) {
  return error instanceof Error && error.message === "Failed to fetch label PDF (404)";
}

export function PrintLabelButton({ onClick, children }: { onClick?: () => void; children: React.ReactNode }) {
  const { batchId, itemId } = useParams();
  const labelKey = batchId && itemId ? `/fulfillment_batches/${batchId}/items/${itemId}/shipping_label` : null;
  const {
    data: labelData,
    error: labelError,
    mutate,
  } = useSWR(labelKey, async (url: string) => fetcher<ShippingLabelData>(url), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: (error) => requestStatus(error) !== 422,
    dedupingInterval: 0,
  });
  const {
    data: imageURL,
    error: imageError,
    mutate: mutateImage,
  } = useSWR(
    labelData?.url ?? null,
    async (url: string) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch label PDF (${response.status})`);
      return URL.createObjectURL(await response.blob());
    },
    { shouldRetryOnError: false, revalidateOnFocus: false, dedupingInterval: 0 },
  );
  // A customer return is rejected with 422. A refused void comes back 200 with status ERROR.
  // Neither one gets a PDF, and asking again will not create one.
  const labelRejected = requestStatus(labelError) === 422;
  const labelUnavailable = labelData?.status === "ERROR" || labelRejected;
  const waitingForLabel = !labelData?.url && !labelUnavailable;

  // A missing file comes back as a new label record (status PENDING, then a new URL).
  // A refused void comes back ERROR and stays there. Refetching a 404 only repeats it.
  // Any other PDF failure keeps that same URL, so fetch the file again or the spinner never clears.
  useEffect(() => {
    if (!labelKey || imageURL || labelUnavailable || (!waitingForLabel && !imageError)) return;

    const id = window.setInterval(() => {
      void mutate();
      if (imageError && !missingLabelFile(imageError)) void mutateImage();
    }, LABEL_RETRY_MS);
    return () => window.clearInterval(id);
  }, [imageError, imageURL, labelKey, labelUnavailable, mutate, mutateImage, waitingForLabel]);

  const isLoading = !imageURL && !labelUnavailable;

  const [showContinue, setShowContinue] = useState(false);
  return (
    <>
      {labelRejected && (
        <Alert severity="error">Shipping label unavailable. Packing cannot continue until a label is available.</Alert>
      )}
      <Button
        onClick={() => {
          if (imageURL) printInWindowSafariMobileHack(imageURL);
          if (onClick) setShowContinue(true);
        }}
        disabled={!imageURL}
      >
        {isLoading ? (
          <>
            Retrieving Shipping Label...
            <CircularProgress size={20} sx={{ ml: 2 }} />
          </>
        ) : labelUnavailable ? (
          "Shipping label unavailable"
        ) : (
          children
        )}
      </Button>
      {labelData?.status === "ERROR" && labelData.error && (
        <Typography color="error" variant="body2">
          {labelData.error}
        </Typography>
      )}
      {showContinue && (
        <Button variant={"contained"} onClick={onClick}>
          Continue
        </Button>
      )}
    </>
  );
}
